import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { API } from '@/lib/api';
import { stripLocale, localizedPath } from '@/lib/locale';

const SITE_NAME = 'Sila Experiences';
// Origine canonica: si adatta automaticamente al dominio servito (oggi 91.98.69.63.nip.io,
// domani www.silaexperiences.com) usando window.location a runtime. In build/prerender
// (localhost) usa REACT_APP_SITE_ORIGIN se presente, altrimenti il fallback qui sotto.
// >> Quando il dominio finale sarà attivo, non serve toccare nulla: verrà rilevato da solo.
const FALLBACK_ORIGIN = (process.env.REACT_APP_SITE_ORIGIN || 'https://silaexperiences.com').replace(/\/$/, '');
const _runtimeOrigin = (typeof window !== 'undefined' && window.location
  && !/^(localhost|127\.0\.0\.1|0\.0\.0\.0)/.test(window.location.hostname))
  ? window.location.origin.replace(/\/$/, '') : '';
export const SITE_ORIGIN = _runtimeOrigin || FALLBACK_ORIGIN;
const DEFAULT_DESC = 'Vivi la Sila: hotel, ristoranti, esperienze ed eventi selezionati nel cuore verde della Calabria.';
const DEFAULT_IMG = 'https://images.pexels.com/photos/20450281/pexels-photo-20450281.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=630&w=1200';

// Cache in-memory degli override SEO per path (evita refetch a ogni navigazione).
const _seoCache = new Map();

function normPath(p) {
  let s = (p || '/').trim();
  if (!s.startsWith('/')) s = '/' + s;
  if (s.length > 1 && s.endsWith('/')) s = s.replace(/\/+$/, '');
  return s || '/';
}

async function fetchOverride(path) {
  const key = normPath(path);
  if (_seoCache.has(key)) return _seoCache.get(key);
  try {
    const res = await fetch(`${API}/seo/resolve?path=${encodeURIComponent(key)}`);
    if (!res.ok) return null;
    const data = await res.json();
    _seoCache.set(key, data);
    return data;
  } catch {
    return null;
  }
}

// Etichette per il breadcrumb automatico (primo livello di percorso).
const BREADCRUMB_LABELS = {
  'alloggi': 'Alloggi', 'esperienze': 'Esperienze', 'dove-mangiare': 'Dove Mangiare',
  'shop': 'Shop', 'eventi': 'Eventi', 'servizi': 'Servizi', 'blog': 'Magazine',
  'il-progetto': 'Il Progetto', 'il-parco-della-sila': 'Il Parco della Sila', 'la-sila': 'La Sila',
  'vacanza': 'Componi la vacanza', 'parchi': 'Parchi', 'educational': 'Educational',
  'contatti': 'Contatti', 'chi-siamo': 'Chi siamo', 'tour-nei-borghi': 'Tour nei Borghi',
  'struttura': 'Struttura', 'negozio': 'Negozio',
};

function prettify(seg) {
  try { seg = decodeURIComponent(seg); } catch { /* noop */ }
  return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

// Costruisce un BreadcrumbList JSON-LD dal pathname (Home > Sezione > …).
function autoBreadcrumb(pathname, origin) {
  const segs = (pathname || '/').split('/').filter(Boolean);
  if (!segs.length) return null;
  const items = [{ name: 'Home', path: '/' }];
  let acc = '';
  segs.forEach((s) => { acc += `/${s}`; items.push({ name: BREADCRUMB_LABELS[s] || prettify(s), path: acc }); });
  return breadcrumbJsonLd(items, origin);
}

/**
 * SEO centralizzata: title, description, canonical, Open Graph, Twitter card,
 * hreflang IT/EN e JSON-LD strutturato.
 *
 * Gli override impostati dal CRM (gestore SEO) hanno la PRECEDENZA sulle props di
 * pagina: title/description/image/canonical/keywords/noindex definiti da admin
 * vincono, così i "testi futuri" si gestiscono senza toccare le singole pagine.
 */
export function Seo({ title, description, image, path, type = 'website', jsonLd, noindex = false, keywords }) {
  const { i18n } = useTranslation();
  const lang = ['en', 'es'].includes(i18n.language) ? i18n.language : 'it';
  // Percorso logico SENZA prefisso lingua (per canonical + alternate coerenti).
  const rawPath = path || (typeof window !== 'undefined' ? window.location.pathname : '/');
  const logical = stripLocale(rawPath);
  const [ov, setOv] = useState(() => _seoCache.get(normPath(logical)) || null);

  useEffect(() => {
    let alive = true;
    fetchOverride(logical).then((d) => { if (alive && d) setOv(d); });
    return () => { alive = false; };
  }, [logical]);

  // URL correnti/alternate sul dominio canonico di produzione.
  const url = SITE_ORIGIN + localizedPath(logical, lang);
  const altIt = SITE_ORIGIN + localizedPath(logical, 'it');
  const altEn = SITE_ORIGIN + localizedPath(logical, 'en');
  const altEs = SITE_ORIGIN + localizedPath(logical, 'es');
  const origin = SITE_ORIGIN;
  const o = ov || {};
  const siteName = o.site_name || SITE_NAME;

  // Precedenza: override DB (se valorizzato) > prop > default.
  // Evitiamo il doppio brand: se il title della pagina contiene gia' il site name
  // non appendiamo il suffisso.
  const fullTitle = o.title
    ? o.title
    : (title
        ? (title.includes(siteName) ? title : `${title} · ${siteName}`)
        : `${siteName} — Vivi la Sila`);
  const desc = o.description || description || DEFAULT_DESC;
  const img = o.og_image || image || DEFAULT_IMG;
  const canonicalUrl = o.canonical || url;
  const noindexEff = noindex || !!o.noindex;
  const kw = (Array.isArray(o.keywords) && o.keywords.length) ? o.keywords : (keywords || []);
  const breadcrumb = autoBreadcrumb(logical, origin);

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {kw.length > 0 && <meta name="keywords" content={kw.join(', ')} />}
      {noindexEff && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonicalUrl} />
      <link rel="alternate" hrefLang="it" href={altIt} />
      <link rel="alternate" hrefLang="en" href={altEn} />
      <link rel="alternate" hrefLang="es" href={altEs} />
      <link rel="alternate" hrefLang="x-default" href={altIt} />
      {/* Open Graph */}
      <meta property="og:site_name" content={siteName} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:locale" content={lang === 'en' ? 'en_US' : lang === 'es' ? 'es_ES' : 'it_IT'} />
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />
      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
      {breadcrumb && <script type="application/ld+json">{JSON.stringify(breadcrumb)}</script>}
    </Helmet>
  );
}

export const organizationJsonLd = (origin) => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: origin,
  logo: `${origin}/favicon.ico`,
  description: DEFAULT_DESC,
  areaServed: 'Sila, Calabria, IT',
  sameAs: [],
});

export const websiteJsonLd = (origin) => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: origin,
  inLanguage: ['it', 'en'],
});

// --- Helper JSON-LD aggiuntivi (Product / Event / LocalBusiness / Breadcrumb) ---

export const productJsonLd = ({ name, description, image, price, currency = 'EUR', url, availability = 'InStock', brand = SITE_NAME }) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name,
  description,
  image,
  brand: { '@type': 'Brand', name: brand },
  ...(price != null ? {
    offers: {
      '@type': 'Offer',
      price: String(price),
      priceCurrency: currency,
      availability: `https://schema.org/${availability}`,
      url,
    },
  } : {}),
});

export const eventJsonLd = ({ name, description, image, startDate, endDate, url, location, price, currency = 'EUR' }) => ({
  '@context': 'https://schema.org',
  '@type': 'Event',
  name,
  description,
  image,
  ...(startDate ? { startDate } : {}),
  ...(endDate ? { endDate } : {}),
  eventStatus: 'https://schema.org/EventScheduled',
  eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
  ...(location ? { location: { '@type': 'Place', name: location, address: location } } : {}),
  ...(price != null ? { offers: { '@type': 'Offer', price: String(price), priceCurrency: currency, url } } : {}),
});

export const localBusinessJsonLd = ({ name, description, image, url, telephone, street, city, region, postalCode, country = 'IT', priceRange = '€€' }) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name,
  description,
  image,
  url,
  ...(telephone ? { telephone } : {}),
  priceRange,
  address: {
    '@type': 'PostalAddress',
    ...(street ? { streetAddress: street } : {}),
    ...(city ? { addressLocality: city } : {}),
    ...(region ? { addressRegion: region } : {}),
    ...(postalCode ? { postalCode } : {}),
    addressCountry: country,
  },
});

export const breadcrumbJsonLd = (items = [], origin = '') => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    item: it.path ? `${origin}${it.path}` : undefined,
  })),
});
